import { ref, computed, onMounted, onUnmounted } from 'vue'
import api from '../utils/api'

const QUEUE_KEY = 'offline_queue'
const CACHE_KEY_VOLUNTEERS = 'cached_volunteers'
const CACHE_KEY_CLIENTS = 'cached_clients'

/**
 * Composable for offline sync functionality
 */
export function useOfflineSync() {
  const isOnline = ref(navigator.onLine)
  const isSyncing = ref(false)
  const pendingOperations = ref([])
  const lastSyncTime = ref(null)

  // Load pending operations from localStorage
  function loadQueue() {
    try {
      const saved = localStorage.getItem(QUEUE_KEY)
      pendingOperations.value = saved ? JSON.parse(saved) : []
    } catch {
      pendingOperations.value = []
    }
  }

  // Save queue to localStorage
  function saveQueue() {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(pendingOperations.value))
  }

  // Add operation to queue
  function queueOperation(type, data) {
    const operation = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      data,
      timestamp: new Date().toISOString()
    }

    pendingOperations.value.push(operation)
    saveQueue()

    // Try to sync immediately if online
    if (isOnline.value) {
      syncQueue()
    }

    return operation.id
  }

  // Sync queue with server
  async function syncQueue() {
    if (!isOnline.value || isSyncing.value || pendingOperations.value.length === 0) {
      return
    }

    isSyncing.value = true

    try {
      const response = await api.post('/sync/push', {
        operations: pendingOperations.value
      })

      if (response.success) {
        // Remove synced operations
        const syncedIds = response.results
          .filter(r => r.status === 'created' || r.status === 'completed')
          .map(r => r.opId)

        pendingOperations.value = pendingOperations.value.filter(
          op => !syncedIds.includes(op.id)
        )
        saveQueue()

        lastSyncTime.value = new Date().toISOString()
      }
    } catch (error) {
      console.error('Sync error:', error)
    } finally {
      isSyncing.value = false
    }
  }

  // Cache data for offline use
  function cacheVolunteers(volunteers) {
    localStorage.setItem(CACHE_KEY_VOLUNTEERS, JSON.stringify(volunteers))
  }

  function cacheClients(clients) {
    localStorage.setItem(CACHE_KEY_CLIENTS, JSON.stringify(clients))
  }

  function getCachedVolunteers() {
    try {
      const saved = localStorage.getItem(CACHE_KEY_VOLUNTEERS)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  }

  function getCachedClients() {
    try {
      const saved = localStorage.getItem(CACHE_KEY_CLIENTS)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  }

  // Online/offline event handlers
  function handleOnline() {
    isOnline.value = true
    syncQueue()
  }

  function handleOffline() {
    isOnline.value = false
  }

  // Computed
  const hasPendingOperations = computed(() => pendingOperations.value.length > 0)
  const pendingCount = computed(() => pendingOperations.value.length)

  onMounted(() => {
    loadQueue()
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
  })

  onUnmounted(() => {
    window.removeEventListener('online', handleOnline)
    window.removeEventListener('offline', handleOffline)
  })

  return {
    isOnline,
    isSyncing,
    hasPendingOperations,
    pendingCount,
    lastSyncTime,
    queueOperation,
    syncQueue,
    cacheVolunteers,
    cacheClients,
    getCachedVolunteers,
    getCachedClients
  }
}

export default useOfflineSync
